import React, { useEffect, useState, useRef } from "react";
import Header from "../../components/Header";
import { AUTH_URL, MASTER_URL } from "../../common/common";
import CustomButton from "../../components/AddButton";
import { useNavigate } from "react-router-dom";
import { Column, DriverData } from "../../service/interfaces/Table.interface";
import AdminContainer from "../../containers/Admin";
import CustomTable from "../../components/Table";
import { ApiService } from "../../service/api.service";
import { driver, employee, Plus } from "../../common/svg";

const Driver: React.FC = () => {
  const navigate = useNavigate();
  const apiService = new ApiService();
  const entityId = localStorage.getItem("entity_id");

  const [employeeData, setEmployeeData] = useState<DriverData[]>([]);
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEdit = (item: DriverData) => {
    navigate(`managedriver/${item.driverId}`);
    setActiveMenu(null);
  };

  // const handleDelete = (item: DriverData) => {
  //   if (
  //     window.confirm(
  //       `Are you sure you want to delete ${item.firstname} ${item.lastname}?`
  //     )
  //   ) {
  //     if (item.employeeid) {
  //       deleteEmployee(item.employeeid);
  //     } else {
  //       console.error("Employee ID is undefined");
  //     }
  //   }
  // };

  const deleteEmployee = async (id: string) => {
    try {
      const response = await apiService.deleteAPi(
        `${AUTH_URL}/employee/delete-route?employeeId=${id}`
      );
      if (response.status === "200") {
        getEmployeeData();
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };

  const getEmployeeData = async (isLoadingMore = false) => {
    if (!hasMoreData && isLoadingMore) return;

    setLoading(true);
    try {
      const response: any = await apiService.getApi(
        `${MASTER_URL}/driver/get-route?page=${currentPage}&limit=${pageSize}&search=${search}&entityId=${entityId}`
      );

      if (response.status === "200") {
        const formattedData =
        response.body?.data?.map((employee: any) => ({
          driverName: employee.driverName || "N/A",
          contact: `${employee.phoneNumber}\n${employee.email}` || "N/A",
          address: employee.address || "N/A", // assuming address is available
          licenseNumber: employee.licenseNumber || "N/A", // assuming from backend
          licenseClass: employee.licenseClass || "N/A",
          dob: employee.dob || null,
          driverId: employee.driverId || null,
          gender: employee.gender || "Other", // fallback to "Other" if missing
        })) || [];
        

        const totalRecords =
          parseInt(response.body.pagination?.totalRecords) || 0;
        const currentLoadedItems = isLoadingMore
          ? employeeData.length + formattedData.length
          : formattedData.length;

        setHasMoreData(currentLoadedItems < totalRecords);

        setEmployeeData((prevData) =>
          isLoadingMore ? [...prevData, ...formattedData] : formattedData
        );

        setTotalItems(totalRecords);
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
      setHasMoreData(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMoreData) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getEmployeeData(true);
    }
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search changes
    setHasMoreData(true);
    getEmployeeData();
  }, [search, entityId]);

  const handleAdd = () => {
    navigate("managedriver");
  };

  const handleRowClick = (item: DriverData) => {
    // Navigate to view details if needed
    // navigate(`view/${item.driverId}`);
  };

  const columns: Column<DriverData>[] = [
    { header: "Driver Name", dataKey: "driverName" },
    {
      header: "Contact ",
      dataKey: "contact",
      render: (value: string | null) => (
        <div className="text-sm text-left leading-relaxed">
        {typeof value === 'string' && value.split("\n").map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
      ),
    },
    {
      header: "Address",
      dataKey: "address",
      render: (value: string | null) => (
        <div className="text-sm text-left leading-relaxed">
          {value ?? "N/A"}
        </div>
      ),
    },
    { header: "License Number", dataKey: "licenseNumber" },
    {
      header: "License Class",
      dataKey: "licenseClass",
      render: (value: string | null) => (
        <div className="text-sm text-left leading-relaxed">
          {value ?? "N/A"}
        </div>
      ),
    },
    {
      header: "Actions",
      dataKey: "driverName",
      render: (value, row) => (
        <div className="relative" ref={activeMenu === row.driverId ? menuRef : null}>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              setActiveMenu(activeMenu === row.driverId ? null : row.driverId ?? null);
            }}
          >
            <div className="flex flex-col items-center justify-center h-5">
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            </div>
          </button>

          {/* Dropdown Menu */}
          {activeMenu === row.driverId && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200">
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                }}
              >
                Edit
              </button>
              <button
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  // handleDelete(row);
                  setActiveMenu(null);
                }}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header
        icon={driver}
        title="Driver"
        showSearch={true}
        setSearch={setSearch}
      >
        <CustomButton label="" onClick={handleAdd} icon={Plus} />
      </Header>
      <AdminContainer>
        <CustomTable<DriverData>
          columns={columns}
          data={employeeData}
          onRowClick={handleRowClick}
          loading={loading}
          onLoadMore={handleLoadMore}
        />
      </AdminContainer>
    </div>
  );
};

export default Driver;